import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../../../util/log';
import { types as sdkTypes } from '../../../util/sdkLoader';

const { Money } = sdkTypes;

const SLICE_NAME = 'recipeSubmission';

// Return an array of image ids (same pattern as EditListingPage)
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// Thunks
// Upload single image with temporary ID tracking
export const uploadRecipeImage = createAsyncThunk(
  `${SLICE_NAME}/uploadRecipeImage`,
  async ({ file, tempId }, { rejectWithValue, extra: sdk }) => {
    try {
      const queryParams = {
        expand: true,
        'fields.image': 'variants.square-small,variants.square-small2x,variants.square-medium',
      };

      const response = await sdk.images
        .upload({ image: file }, queryParams)
        .then(resp => {
          const img = resp.data.data;
          return {
            data: { ...img, id: tempId, imageId: img.id, file },
          };
        })
        .catch(e => {
          return rejectWithValue({ id: tempId, error: e });
        });
      
      return response;
    } catch (error) {
      log.error(error, 'upload-recipe-image-failed');
      return rejectWithValue({ id: tempId, error });
    }
  }
);

// Legacy batch upload for backward compatibility
export const uploadRecipeImages = createAsyncThunk(
  `${SLICE_NAME}/uploadRecipeImages`,
  async (files, { dispatch, rejectWithValue }) => {
    try {
      if (!files || !files.length) return [];
      
      const uploadPromises = Array.from(files).map((file, index) => {
        const tempId = `temp_${Date.now()}_${index}`;
        return dispatch(uploadRecipeImage({ file, tempId }));
      });
      
      const results = await Promise.all(uploadPromises);
      return results.map(result => result.payload);
    } catch (error) {
      log.error(error, 'upload-recipe-images-failed');
      return rejectWithValue(error);
    }
  }
);

// Fetch recipes from listings API
export const fetchRecipes = createAsyncThunk(
  `${SLICE_NAME}/fetchRecipes`,
  async (params = {}, { rejectWithValue, extra: sdk }) => {
    try {
      const queryParams = {
        expand: true,
        include: ['author', 'images'],
        'fields.image': 'variants.square-small,variants.square-small2x,variants.square-medium',
        
        per_page: 100,
        ...params,
      };
      const response = await sdk.listings.query({
        pub_listingType: 'recipe',
        ...queryParams,
      });
      const listings = response.data.data;
      const included = response.data.included || [];
      
      // Create a map of included resources by ID and type
      // Use just the UUID string for the key
      const includedMap = new Map();
      included.forEach(item => {
        const id = item.id?.uuid || item.id;
        const key = `${item.type}-${id}`;
        includedMap.set(key, item);
      });

      // Normalize each listing to include nested image data
      const normalizedListings = listings.map(listing => {
        const normalized = { ...listing };
        
        // If listing has image relationships, resolve them from included data
        if (listing.relationships?.images?.data) {
          const imageRelationships = listing.relationships.images.data;
          
          const resolvedImages = imageRelationships.map(imageRel => {
            // Get the UUID from the relationship object
            const imageId = imageRel.id?.uuid || imageRel.id;
            const imageType = imageRel.type;
            
            // Construct the correct key format: type-uuid
            const imageKey = `${imageType}-${imageId}`;
            const imageData = includedMap.get(imageKey);
            
            if (!imageData) {
              console.warn('❌ Image not found for key:', imageKey, 
                'Available image keys:', Array.from(includedMap.keys()).filter(k => k.startsWith('image-')));
            }
            
            return imageData;
          }).filter(Boolean);         
          normalized.images = resolvedImages;
        } else {
          normalized.images = [];
        }
        
        return normalized;
      });

      return normalizedListings;
    } catch (error) {
      log.error(error, 'fetch-recipes-failed');
      return rejectWithValue({
        message: error.message || 'Failed to fetch recipes',
        status: error.status || 500,
      });
    }
  }
);

// Search recipes with filters (server-side filtering)
export const searchRecipes = createAsyncThunk(
  `${SLICE_NAME}/searchRecipes`,
  async ({ searchParams = {}, page = 1, perPage = 24 }, { rejectWithValue, extra: sdk }) => {
    try {
      console.log('🔍 searchRecipes called with:', { searchParams, page, perPage });

      const {
        ingredient,
        category,
        prepTime,
        search,
        userSubmitted,
        ...restParams
      } = searchParams;

      // Build API query parameters
      const apiParams = {
        expand: true,
        include: ['author', 'images'],
        'fields.image': 'variants.square-small,variants.square-small2x,variants.square-medium',
        'fields.listing': [
          'title',
          'description',
          'state',
          'deleted',
          'publicData.listingType',
          'publicData.recipeType',
          'publicData.mainProduct',
          'publicData.category',
          'publicData.ingredients',
          'publicData.instructions',
          'publicData.prepTime',
          'publicData.allergens',
          'publicData.userSubmitted',
        ],
        page,
        per_page: perPage,
        ...restParams,
      };
        apiParams.pub_listingType = 'recipe';

      // Filter by listingType (user-submitted vs bulk-uploaded)
      if (userSubmitted) {
        // Show only user-submitted recipes (have userSubmitted: true)
        apiParams.pub_userSubmitted = true;
      } 
//your listing product recipes product  and recipee
      // Filter by ingredient (recipeType or mainProduct)
      if (ingredient) {
        // API supports filtering by publicData fields
        // We can filter by recipeType OR mainProduct
        apiParams.pub_recipeType = ingredient;
        // Note: If you need to filter by mainProduct too, you might need multiple queries
        // or use keywords search
      }

      // Filter by category (dish type)
      if (category) {
        apiParams.pub_category = category;
      }

      // Filter by prep time
      if (prepTime) {
        // Convert time filter to range format
        if (prepTime === '< 30 min') {
          apiParams.pub_prepTime = 30;
        } else if (prepTime === '30-60 min') {
          apiParams.pub_prepTime = '30,60';
        } else {
          // For any other time format, pass as-is
          apiParams.pub_prepTime = prepTime;
        }
      }

      // Search query (searches in title, description, ingredients)
      if (search) {
        apiParams.keywords = search;
      }

      console.log('📡 API params:', apiParams);

      const response = await sdk.listings.query(apiParams);
      
      const listings = response.data.data;
      const included = response.data.included || [];
      const meta = response.data.meta;

      console.log('✅ API response:', {
        listingsCount: listings.length,
        totalPages: meta.totalPages,
        totalItems: meta.totalItems,
      });

      // Create a map of included resources
      const includedMap = new Map();
      included.forEach(item => {
        const id = item.id?.uuid || item.id;
        const key = `${item.type}-${id}`;
        includedMap.set(key, item);
      });

      // Normalize listings with images
      const normalizedListings = listings.map(listing => {
        const normalized = { ...listing };
        
        if (listing.relationships?.images?.data) {
          const imageRelationships = listing.relationships.images.data;
          const resolvedImages = imageRelationships.map(imageRel => {
            const imageId = imageRel.id?.uuid || imageRel.id;
            const imageType = imageRel.type;
            const imageKey = `${imageType}-${imageId}`;
            return includedMap.get(imageKey);
          }).filter(Boolean);
          
          normalized.images = resolvedImages;
        } else {
          normalized.images = [];
        }
        
        return normalized;
      });

      return {
        recipes: normalizedListings,
        pagination: meta,
      };
    } catch (error) {
      log.error(error, 'search-recipes-failed');
      console.error('❌ Search recipes error:', error);
      return rejectWithValue({
        message: error.message || 'Failed to search recipes',
        status: error.status || 500,
      });
    }
  }
);

export const submitRecipe = createAsyncThunk(
  `${SLICE_NAME}/submitRecipe`,
  async (recipeData, { rejectWithValue, extra: sdk }) => {
    console.log('🚀 submitRecipe thunk STARTED');
    console.log('📦 recipeData received:', recipeData);
    console.log('🔧 sdk available:', !!sdk);
    console.log('🔧 sdk.ownListings available:', !!(sdk && sdk.ownListings));
    
    try {
      const { images, ...rest } = recipeData;
      console.log('📝 Extracted rest:', rest);
      console.log('🖼️ Images:', images);
      const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
      console.log('🔗 Image property:', imageProperty);

      // Prepare recipe data in listing format
      const recipeValues = {
        title: rest.title,
        description: rest.description,
        // Store recipe-specific data in publicData
        publicData: {
          listingType: 'recipe',
          recipeType: rest.recipeType || rest.category || 'general', // Use recipeType first, fallback to category
          mainProduct: rest.category || 'general', // Main ingredient from category field
          ingredients: rest.ingredients,
          instructions: rest.instructions,
          prepTime: rest.prepTime,
          cookTime: rest.cookTime,
          servings: rest.servings,
          difficulty: rest.difficulty,
          authorName: rest.authorName, // Include author name
          userSubmitted: true, // Always set to true for user-submitted recipes
        },
        ...imageProperty,
      };

      console.log('🎯 Final recipeValues for API:', recipeValues);

      // Use the same query parameters pattern as listings
      const queryParams = {
        expand: true,
        include: ['author', 'images'],
        'fields.image': 'variants.square-small,variants.square-small2x,variants.square-medium',
      };
      
      console.log('🔍 Query params:', queryParams);
      console.log('📡 About to call sdk.ownListings.createDraft...');
      
      // Create recipe as a listing (using the listing API)
      const response = await sdk.ownListings.createDraft(recipeValues, queryParams);
      console.log('✅ API call successful:', response);
      
      const recipeId = response.data.data.id;
      console.log('🎉 Recipe created successfully with ID:', recipeId);
      
      // Now publish the recipe (like create new post)
      console.log('📡 About to call sdk.ownListings.publishDraft...');
      const publishResponse = await sdk.ownListings.publishDraft({ id: recipeId }, queryParams);
      console.log('✅ Recipe published successfully:', publishResponse);
      
      return publishResponse.data;
    } catch (error) {
      console.error('❌ API call failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        data: error.data
      });
      log.error(error, 'create-recipe-draft-failed', { recipeData });
      return rejectWithValue({
        message: error.message || 'Failed to create recipe',
        status: error.status || 500,
      });
    }
  }
);

const initialState = {
  uploadInProgress: false,
  uploadError: null,
  submitInProgress: false,
  submitError: null,
  uploadedImages: {}, // Object to store images by temp ID
  uploadedImagesOrder: [], // Array to maintain order
  submittedRecipe: null,
  
  // Recipe fetching state
  fetchRecipesInProgress: false,
  fetchRecipesError: null,
  recipes: [], // Array of recipe listings
  
  // Search recipes state (server-side filtering)
  searchInProgress: false,
  searchError: null,
  searchResults: [], // Array of search result recipes
  searchParams: null, // Current search parameters
  pagination: null, // Pagination metadata from API
};

const recipeSubmissionSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    // Reset upload errors
    clearUploadError: (state) => {
      state.uploadError = null;
    },
    // Remove uploaded image
    removeImage: (state, action) => {
      const imageId = action.payload;
      state.uploadedImagesOrder = state.uploadedImagesOrder.filter(id => id !== imageId);
      delete state.uploadedImages[imageId];
    },
    // Reset form
    resetForm: (state) => {
      state.uploadError = null;
      state.submitError = null;
      state.uploadedImages = {};
      state.uploadedImagesOrder = [];
      state.submittedRecipe = null;
    },
    // Clear search results
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchParams = null;
      state.pagination = null;
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    // Single image upload cases
    builder
      .addCase(uploadRecipeImage.pending, (state, action) => {
        const tempId = action.meta.arg.tempId;
        state.uploadInProgress = true;
        state.uploadError = null;
        
        // Add temporary image object
        state.uploadedImages[tempId] = {
          id: tempId,
          file: action.meta.arg.file,
          status: 'pending',
        };
        state.uploadedImagesOrder = state.uploadedImagesOrder.concat([tempId]);
      })
      .addCase(uploadRecipeImage.fulfilled, (state, action) => {
        const { id, ...imageData } = action.payload.data;
        state.uploadedImages[id] = {
          id,
          ...imageData,
          status: 'uploaded',
        };
        state.uploadInProgress = false;
        state.uploadError = null;
      })
      .addCase(uploadRecipeImage.rejected, (state, action) => {
        const { id, error } = action.payload;
        state.uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
        delete state.uploadedImages[id];
        state.uploadError = error;
        state.uploadInProgress = false;
      })
      
      // Batch upload cases (legacy)
      .addCase(uploadRecipeImages.pending, (state) => {
        state.uploadInProgress = true;
        state.uploadError = null;
      })
      .addCase(uploadRecipeImages.fulfilled, (state, action) => {
        action.payload.forEach(image => {
          state.uploadedImages[image.id] = image;
          if (!state.uploadedImagesOrder.includes(image.id)) {
            state.uploadedImagesOrder.push(image.id);
          }
        });
        state.uploadInProgress = false;
      })
      .addCase(uploadRecipeImages.rejected, (state, action) => {
        state.uploadError = action.payload;
        state.uploadInProgress = false;
      })
      
      // Recipe submission cases
      .addCase(submitRecipe.pending, (state) => {
        state.submitInProgress = true;
        state.submitError = null;
      })
      .addCase(submitRecipe.fulfilled, (state, action) => {
        state.submitInProgress = false;
        state.submittedRecipe = action.payload;
      })
      .addCase(submitRecipe.rejected, (state, action) => {
        state.submitInProgress = false;
        state.submitError = action.payload;
      })
      
      // Fetch recipes cases
      .addCase(fetchRecipes.pending, (state) => {
        state.fetchRecipesInProgress = true;
        state.fetchRecipesError = null;
      })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        state.fetchRecipesInProgress = false;
        state.recipes = action.payload;
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        state.fetchRecipesInProgress = false;
        state.fetchRecipesError = action.payload;
      })
      
      // Search recipes cases
      .addCase(searchRecipes.pending, (state, action) => {
        state.searchInProgress = true;
        state.searchError = null;
        state.searchParams = action.meta.arg.searchParams;
      })
      .addCase(searchRecipes.fulfilled, (state, action) => {
        state.searchInProgress = false;
        state.searchResults = action.payload.recipes;
        state.pagination = action.payload.pagination;
      })
      .addCase(searchRecipes.rejected, (state, action) => {
        state.searchInProgress = false;
        state.searchError = action.payload;
      });
  },
});

// Actions
export const { clearUploadError, removeImage, resetForm, clearSearchResults } = recipeSubmissionSlice.actions;

// Selectors
const selectRecipeSubmissionState = (state) => state[SLICE_NAME];

export const selectUploadedImages = (state) => {
  const images = selectRecipeSubmissionState(state).uploadedImages;
  const order = selectRecipeSubmissionState(state).uploadedImagesOrder;
  return order.map(id => images[id]).filter(Boolean);
};
export const selectUploadInProgress = (state) => selectRecipeSubmissionState(state).uploadInProgress;
export const selectUploadError = (state) => selectRecipeSubmissionState(state).uploadError;
export const selectSubmitInProgress = (state) => selectRecipeSubmissionState(state).submitInProgress;
export const selectSubmitError = (state) => selectRecipeSubmissionState(state).submitError;
export const selectSubmittedRecipe = (state) => selectRecipeSubmissionState(state).submittedRecipe;
export const selectUploadedImagesOrder = (state) => selectRecipeSubmissionState(state).uploadedImagesOrder;

// Recipe fetching selectors
export const selectRecipes = (state) => selectRecipeSubmissionState(state).recipes;
export const selectFetchRecipesInProgress = (state) => selectRecipeSubmissionState(state).fetchRecipesInProgress;
export const selectFetchRecipesError = (state) => selectRecipeSubmissionState(state).fetchRecipesError;

// Search recipes selectors
export const selectSearchResults = (state) => selectRecipeSubmissionState(state).searchResults;
export const selectSearchInProgress = (state) => selectRecipeSubmissionState(state).searchInProgress;
export const selectSearchError = (state) => selectRecipeSubmissionState(state).searchError;
export const selectSearchParams = (state) => selectRecipeSubmissionState(state).searchParams;
export const selectPagination = (state) => selectRecipeSubmissionState(state).pagination;

export default recipeSubmissionSlice.reducer;